import React, {
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import AddressFields from './AddressFields';
import { addressFieldProp } from './AddressFieldItem';
import useAddressy from '../../../hooks/useAddressy';
import { getAddressLines } from '../../../tools/address';

const textProp = PropTypes.shape({
  key: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
});

const addressProps = PropTypes.shape({
  address1: PropTypes.string,
  address2: PropTypes.string,
  city: PropTypes.string,
  country: PropTypes.string,
  district: PropTypes.string,
  firstName: PropTypes.string,
  isAPOFPO: PropTypes.bool,
  isBillingAllowed: PropTypes.bool,
  isDefaultShipping: PropTypes.bool,
  isShippingAllowed: PropTypes.bool,
  lastName: PropTypes.string,
  name: PropTypes.string,
  phoneticName: PropTypes.string,
  poBox: PropTypes.bool,
  postalCode: PropTypes.string,
  prefecture: PropTypes.string,
  province: PropTypes.string,
  state: PropTypes.string,
  title: PropTypes.string,
  zone: PropTypes.string,
});

const addressConfigProps = PropTypes.shape({
  id: PropTypes.string,
  code: PropTypes.string,
  key: PropTypes.string,
  name: PropTypes.string,
  region: PropTypes.string,
  storeLocator: PropTypes.bool,
  billingCountry: PropTypes.bool,
  shippingCountry: PropTypes.bool,
  fields: PropTypes.arrayOf(addressFieldProp),
  visibility: PropTypes.shape({
    firstName: PropTypes.bool,
    lastname: PropTypes.bool,
    country: PropTypes.bool,
    address1: PropTypes.bool,
    address2: PropTypes.bool,
    address3: PropTypes.bool,
    poBox: PropTypes.bool,
    city: PropTypes.bool,
    APOFPORegion: PropTypes.bool,
    state: PropTypes.bool,
    prefecture: PropTypes.bool,
    province: PropTypes.bool,
    postalCode: PropTypes.bool,
    region: PropTypes.bool,
    phone: PropTypes.bool,
    name: PropTypes.bool,
    phoneticName: PropTypes.bool,
    zone: PropTypes.bool,
    district: PropTypes.bool,
  }),
});

const propTypes = {
  address: addressProps,
  type: PropTypes.oneOf(['shipping', 'billing']).isRequired,
  fieldInFix: PropTypes.oneOf(['homeDelivery', 'billing']).isRequired,
  addressConfig: addressConfigProps,
  addressyKey: PropTypes.string,
  addressyEnabled: PropTypes.bool,
  manualEntryItemLabel: textProp,
  countryCode: PropTypes.string,
  errorTmnts: PropTypes.shape({}),
  onSelectAddress: PropTypes.func,
};

const defaultProps = {
  address: undefined,
  addressConfig: undefined,
  addressyEnabled: undefined,
  manualEntryItemLabel: undefined,
  addressyKey: undefined,
  countryCode: 'USA',
  errorTmnts: undefined,
  onSelectAddress: undefined,
};

export default function AddressForm(props) {
  const {
    addressConfig,
    addressyEnabled,
    type,
    manualEntryItemLabel,
    addressyKey,
    countryCode,
    fieldInFix,
    errorTmnts,
    onSelectAddress,
  } = props;
  const nameFields = useMemo(() => [`${type}-first-name`, `${type}-last-name`], [type]);
  const countryFields = useMemo(() => [`${type}-country`], [type]);
  const { reset } = useFormContext();

  const onPopulateData = (selectedItem) => {
    let postalCode = selectedItem.PostalCode;
    if (countryCode === 'US') {
      postalCode = postalCode.slice(0, 5);
    }
    const { address1, address2 } = getAddressLines(countryCode, selectedItem);

    reset((prev) => ({
      ...prev,
      addresses: {
        ...prev.addresses,
        [fieldInFix]: {
          ...prev.addresses?.[fieldInFix],
          state: selectedItem.ProvinceCode,
          postalCode,
          city: selectedItem.City,
          address1,
          address2,
        },
      },
    }));
    onSelectAddress?.(true);
  };

  useAddressy({
    isEnabled: addressyEnabled,
    apiKey: addressyKey,
    manualEntryItemLabel,
    onPopulateData,
    countryCode,
    type,
  });

  const countryGroupFields = useMemo(
    () => {
      if (!addressConfig) return [];
      return addressConfig.fields.filter((field) => countryFields.includes(field.id));
    },
    [addressConfig, countryFields],
  );

  const isCountryGroupVisible = useMemo(() => countryGroupFields.some(
    (field) => field.visibility,
  ), [countryGroupFields]);

  const nameGroupFields = useMemo(
    () => {
      if (!addressConfig) return [];
      return addressConfig.fields.filter((field) => nameFields.includes(field.id));
    },
    [addressConfig, nameFields],
  );

  const addressGroupFields = useMemo(
    () => {
      if (!addressConfig) return [];
      return addressConfig.fields.filter(
        (field) => !nameFields.concat(countryFields).includes(field.id),
      );
    },
    [addressConfig, nameFields, countryFields],
  );

  return (
    <section id="address-form-wrapper" data-testid={`${type}-form`}>
      {countryGroupFields.length > 0 && (
        <fieldset className={`field-group ${isCountryGroupVisible ? '' : 'no-border'}`}>
          <AddressFields
            fields={countryGroupFields}
            countryCode={countryCode}
            errorTmnts={errorTmnts}
          />
        </fieldset>
      )}
      {nameGroupFields.length > 0 && (
        <fieldset className="field-group">
          <AddressFields
            fields={nameGroupFields}
            countryCode={countryCode}
            errorTmnts={errorTmnts}
          />
        </fieldset>
      )}
      {addressGroupFields.length > 0 && (
        <fieldset className="field-group">
          <AddressFields
            fields={addressGroupFields}
            countryCode={countryCode}
            errorTmnts={errorTmnts}
          />
        </fieldset>
      )}
    </section>
  );
}

AddressForm.propTypes = propTypes;
AddressForm.defaultProps = defaultProps;
